import React, { useState } from 'react';
import { signInWithGoogle, signInWithEmail, signUpWithEmail, resetPassword, changePasswordDirectly, changePasswordWithReauth } from '../services/auth';
import { motion, AnimatePresence } from 'motion/react';
import { APP_ID } from '../constants';

interface AuthModalProps {
    isOpen: boolean;
    onClose: () => void;
    onSuccess: (user: any) => void;
    onError: (err: { message: string; domain?: string; type?: 'firebase' | 'gcp' }) => void;
    initialMode?: 'options' | 'login' | 'signup' | 'reset' | 'confirmReset' | 'directChange';
    resetToken?: string;
    initialEmail?: string;
    userId?: string;
}

const AuthModal: React.FC<AuthModalProps> = ({ isOpen, onClose, onSuccess, onError, initialMode = 'options', resetToken = '', initialEmail = '', userId = '' }) => {
    const [mode, setMode] = useState<'options' | 'login' | 'signup' | 'reset' | 'confirmReset' | 'directChange'>(initialMode);
    const [email, setEmail] = useState(initialEmail);
    const [password, setPassword] = useState('');
    const [newPassword, setNewPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [showPassword, setShowPassword] = useState(false);
    const [showNewPassword, setShowNewPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);
    const [token, setToken] = useState(resetToken);
    const [name, setName] = useState('');
    const [loading, setLoading] = useState(false);
    const [authErr, setAuthErr] = useState<string | null>(null);

    React.useEffect(() => {
        if (isOpen) {
            if (initialMode) setMode(initialMode);
            if (resetToken) setToken(resetToken);
            if (initialEmail) setEmail(initialEmail);
            setPassword('');
            setNewPassword('');
            setConfirmPassword('');
            setAuthErr(null);
        }
    }, [isOpen, initialMode, resetToken, initialEmail]);

    const handleGoogleLogin = async () => {
        setLoading(true);
        setAuthErr(null);
        try {
            const result = await signInWithGoogle(APP_ID);
            if (result) {
                localStorage.setItem('lastLoginProvider', 'google');
                onSuccess(result.user);
            }
            onClose();
        } catch (err: any) {
            const isNetworkErr = err.code === 'auth/network-request-failed' || err.message?.includes('network-request-failed');
            const msg = isNetworkErr 
                ? "Network error: Firebase servers are unreachable. Please check your connection or disable AdBlockers/DuckDuckGo Privacy Essentials for this site."
                : (err.message || "Google login failed");
            
            onError({ 
                message: msg,
                domain: window.location.hostname,
                type: (err.code?.includes('domain') || isNetworkErr) ? 'firebase' : undefined
            });
        } finally {
            setLoading(false);
        }
    };

    const handleEmailAction = async (e: React.FormEvent) => {
        e.preventDefault();
        setLoading(true);
        setAuthErr(null);
        try {
            if (mode === 'directChange') {
                if (!userId) {
                    setAuthErr("You must be logged in to change your password.");
                    setLoading(false);
                    return;
                }
                if (!password) {
                    setAuthErr("Please enter your current password.");
                    setLoading(false);
                    return;
                }
                if (!newPassword || newPassword.length < 6) {
                    setAuthErr("New password must be at least 6 characters long.");
                    setLoading(false);
                    return;
                }
                if (newPassword !== confirmPassword) {
                    setAuthErr("Passwords do not match.");
                    setLoading(false);
                    return;
                }

                await changePasswordWithReauth(password, newPassword);

                setAuthErr("SUCCESS: Your password has been updated successfully!");
                setPassword('');
                setNewPassword('');
                setConfirmPassword('');
                setLoading(false);
                setTimeout(() => {
                    onClose();
                }, 2000);
                return;
            }

            if (mode === 'confirmReset') {
                if (!token) {
                    setAuthErr("Reset token is missing. Please click the link in your email again.");
                    setLoading(false);
                    return;
                }
                if (!newPassword || newPassword.length < 6) {
                    setAuthErr("Password must be at least 6 characters long.");
                    setLoading(false);
                    return;
                }
                if (newPassword !== confirmPassword) {
                    setAuthErr("Passwords do not match.");
                    setLoading(false);
                    return;
                }

                const response = await fetch("/api/auth/confirm-reset", {
                    method: "POST",
                    headers: { "Content-Type": "application/json" },
                    body: JSON.stringify({ token, newPassword })
                });
                const data = await response.json();
                if (!response.ok || !data.success) {
                    throw new Error(data.error || "Failed to update password. Reset link may have expired.");
                }

                if (data.email) {
                    setEmail(data.email);
                }
                setPassword(newPassword);
                setAuthErr("SUCCESS: Password updated! You can now log in with one click below.");
                setMode('login');
                setNewPassword('');
                setConfirmPassword('');
                setLoading(false);
                return;
            }

            if (mode === 'login') {
                const result = await signInWithEmail(email, password);
                localStorage.setItem('lastLoginProvider', 'email');
                onSuccess(result.user);
            } else if (mode === 'signup') {
                const result = await signUpWithEmail(email, password, name);
                localStorage.setItem('lastLoginProvider', 'email');
                onSuccess(result.user);
            } else if (mode === 'reset') {
                try {
                    await resetPassword(email);
                    setAuthErr("SUCCESS: A password reset link has been sent to your email. Please check your inbox and spam folder.");
                } catch (err: any) {
                    setAuthErr(err.message || "Failed to send reset link. Please try again later.");
                }
                setLoading(false);
                return;
            }
            onClose();
        } catch (err: any) {
            const isNetworkErr = err.code === 'auth/network-request-failed' || err.message?.includes('network-request-failed');
            
            if (isNetworkErr) {
                setAuthErr("Network error: Firebase servers are unreachable. Please check your connection or disable AdBlockers.");
            } else if (err.code === 'auth/email-already-in-use') {
                setAuthErr("This email is already registered (possibly on one of our sister apps). Your account works across all our platforms! Please login instead.");
            } else if (err.code === 'auth/invalid-credential' || err.code === 'auth/wrong-password') {
                setAuthErr("Invalid email or password. Please try again.");
            } else {
                setAuthErr(err.message || "Authentication failed");
            }
        } finally {
            setLoading(false);
        }
    };

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm">
            <motion.div 
                initial={{ opacity: 0, scale: 0.95, y: 20 }}
                animate={{ opacity: 1, scale: 1, y: 0 }}
                exit={{ opacity: 0, scale: 0.95, y: 20 }}
                className="bg-white dark:bg-stone-900 w-full max-w-md rounded-2xl shadow-2xl overflow-hidden border border-stone-200 dark:border-stone-800"
            >
                <div className="bg-amber-600 p-6 flex items-center justify-between">
                    <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-white/20 rounded-lg flex items-center justify-center text-white text-xl">
                            <i className="fa-solid fa-vault"></i>
                        </div>
                        <div>
                            <h2 className="text-xl font-bold text-white">Cayce Chronicles</h2>
                            <p className="text-amber-100 text-xs">Unlock Ancient Wisdom</p>
                        </div>
                    </div>
                    <button onClick={onClose} className="text-white/80 hover:text-white transition-colors">
                        <i className="fa-solid fa-xmark text-lg"></i>
                    </button>
                </div>

                <div className="p-8">
                    <AnimatePresence mode="wait">
                        {mode === 'options' ? (
                            <motion.div 
                                key="options"
                                initial={{ opacity: 0, x: -20 }}
                                animate={{ opacity: 1, x: 0 }}
                                exit={{ opacity: 0, x: 20 }}
                                className="space-y-4"
                            >
                                <button
                                    onClick={handleGoogleLogin}
                                    disabled={loading}
                                    className="w-full h-12 flex items-center justify-center gap-3 bg-white hover:bg-stone-50 text-stone-700 border border-stone-300 rounded-xl font-medium transition-all shadow-sm disabled:opacity-50"
                                >
                                    {loading ? (
                                        <i className="fa-solid fa-circle-notch fa-spin"></i>
                                    ) : (
                                        <>
                                            <svg className="w-5 h-5" viewBox="0 0 18 18" xmlns="http://www.w3.org/2000/svg">
                                                <path d="M17.64 9.20455C17.64 8.56636 17.5827 7.95273 17.4764 7.36364H9V10.845H13.8436C13.635 11.97 13.0009 12.9232 12.0477 13.5614V15.8195H14.9564C16.6582 14.2527 17.64 11.9455 17.64 9.20455Z" fill="#4285F4"/>
                                                <path d="M9 18C11.43 18 13.4673 17.1941 14.9564 15.8195L12.0477 13.5614C11.2418 14.1014 10.2109 14.4205 9 14.4205C6.65591 14.4205 4.67182 12.8373 3.96409 10.71H0.957273V13.0418C2.43818 15.9832 5.48182 18 9 18Z" fill="#34A853"/>
                                                <path d="M3.96409 10.71C3.78409 10.17 3.68182 9.59318 3.68182 9C3.68182 8.40682 3.78409 7.83 3.96409 7.29V4.95818H0.957273C0.347727 6.17318 0 7.54773 0 9C0 10.4523 0.347727 11.8268 0.957273 13.0418L3.96409 10.71Z" fill="#FBBC05"/>
                                                <path d="M9 3.57955C10.3214 3.57955 11.5077 4.03364 12.4405 4.92545L15.0218 2.34409C13.4632 0.891818 11.4259 0 9 0C5.48182 0 2.43818 2.01682 0.957273 4.95818L3.96409 7.29C4.67182 5.16273 6.65591 3.57955 9 3.57955Z" fill="#EA4335"/>
                                            </svg>
                                            <span>Continue with Google</span>
                                        </>
                                    )}
                                </button>

                                <div className="relative py-4 flex items-center justify-center">
                                    <div className="absolute inset-0 flex items-center"><div className="w-full border-t border-stone-200"></div></div>
                                    <span className="relative px-4 bg-white dark:bg-stone-900 text-stone-400 text-xs uppercase tracking-widest">or</span>
                                </div>

                                <button
                                    onClick={() => setMode('login')}
                                    className="w-full h-12 flex items-center justify-center gap-3 bg-stone-100 dark:bg-stone-800 hover:bg-stone-200 dark:hover:bg-stone-700 text-stone-700 dark:text-stone-200 rounded-xl font-medium transition-all"
                                >
                                    <i className="fa-solid fa-envelope"></i>
                                    <span>Continue with Email</span>
                                </button>
                            </motion.div>
                        ) : (
                            <motion.form 
                                key={mode}
                                initial={{ opacity: 0, x: 20 }}
                                animate={{ opacity: 1, x: 0 }}
                                exit={{ opacity: 0, x: -20 }}
                                onSubmit={handleEmailAction}
                                className="space-y-4"
                            >
                                {mode !== 'directChange' && (
                                    <button 
                                        type="button"
                                        onClick={() => setMode('options')}
                                        className="text-stone-500 hover:text-stone-800 text-xs flex items-center gap-2 mb-2"
                                    >
                                        <i className="fa-solid fa-arrow-left"></i>
                                        <span>Back to options</span>
                                    </button>
                                )}

                                {mode === 'confirmReset' || mode === 'directChange' ? (
                                    <>
                                        <div className="mb-2">
                                            <h3 className="text-base font-bold text-stone-900 dark:text-stone-100">
                                                {mode === 'directChange' ? 'Change Your Password' : 'Reset Your Password'}
                                            </h3>
                                            <p className="text-xs text-stone-500 dark:text-stone-400">
                                                {mode === 'directChange' ? 'Enter your current password and your new password below.' : 'Please enter and confirm your new password.'}
                                            </p>
                                        </div>

                                        {mode === 'directChange' && (
                                            <div>
                                                <label className="block text-xs font-semibold text-stone-500 uppercase tracking-wider mb-1">Current Password</label>
                                                <div className="relative">
                                                    <i className="fa-solid fa-lock absolute left-3 top-1/2 -translate-y-1/2 text-stone-400 text-xs"></i>
                                                    <input 
                                                        type={showPassword ? "text" : "password"}
                                                        required
                                                        value={password}
                                                        onChange={(e) => setPassword(e.target.value)}
                                                        placeholder="Your current password"
                                                        className="w-full pl-9 pr-10 py-3 bg-white dark:bg-stone-800 border border-stone-300 dark:border-stone-700 rounded-xl text-sm focus:ring-2 focus:ring-amber-500/20 focus:border-amber-500 outline-none transition-all text-stone-900 dark:text-stone-100 placeholder:text-stone-400"
                                                    />
                                                    <button 
                                                        type="button"
                                                        onClick={() => setShowPassword(!showPassword)}
                                                        aria-label={showPassword ? "Hide password" : "Reveal password"}
                                                        className="absolute right-3 top-1/2 -translate-y-1/2 text-stone-400 hover:text-stone-600 dark:hover:text-stone-200 text-xs transition-colors p-1"
                                                    >
                                                        <i className={`fa-solid ${showPassword ? 'fa-eye-slash' : 'fa-eye'}`}></i>
                                                    </button>
                                                </div>
                                            </div>
                                        )}

                                        <div>
                                            <label className="block text-xs font-semibold text-stone-500 uppercase tracking-wider mb-1">New Password</label>
                                            <div className="relative">
                                                <i className="fa-solid fa-key absolute left-3 top-1/2 -translate-y-1/2 text-stone-400 text-xs"></i>
                                                <input 
                                                    type={showNewPassword ? "text" : "password"}
                                                    required
                                                    minLength={6}
                                                    value={newPassword}
                                                    onChange={(e) => setNewPassword(e.target.value)}
                                                    placeholder="At least 6 characters"
                                                    className="w-full pl-9 pr-10 py-3 bg-white dark:bg-stone-800 border border-stone-300 dark:border-stone-700 rounded-xl text-sm focus:ring-2 focus:ring-amber-500/20 focus:border-amber-500 outline-none transition-all text-stone-900 dark:text-stone-100 placeholder:text-stone-400"
                                                />
                                                <button 
                                                    type="button"
                                                    onClick={() => setShowNewPassword(!showNewPassword)}
                                                    aria-label={showNewPassword ? "Hide password" : "Reveal password"}
                                                    className="absolute right-3 top-1/2 -translate-y-1/2 text-stone-400 hover:text-stone-600 dark:hover:text-stone-200 text-xs transition-colors p-1"
                                                >
                                                    <i className={`fa-solid ${showNewPassword ? 'fa-eye-slash' : 'fa-eye'}`}></i>
                                                </button>
                                            </div>
                                        </div>

                                        <div>
                                            <label className="block text-xs font-semibold text-stone-500 uppercase tracking-wider mb-1">Confirm New Password</label>
                                            <div className="relative">
                                                <i className="fa-solid fa-lock absolute left-3 top-1/2 -translate-y-1/2 text-stone-400 text-xs"></i>
                                                <input 
                                                    type={showConfirmPassword ? "text" : "password"}
                                                    required
                                                    minLength={6}
                                                    value={confirmPassword}
                                                    onChange={(e) => setConfirmPassword(e.target.value)}
                                                    placeholder="Re-enter new password"
                                                    className="w-full pl-9 pr-10 py-3 bg-white dark:bg-stone-800 border border-stone-300 dark:border-stone-700 rounded-xl text-sm focus:ring-2 focus:ring-amber-500/20 focus:border-amber-500 outline-none transition-all text-stone-900 dark:text-stone-100 placeholder:text-stone-400"
                                                />
                                                <button 
                                                    type="button"
                                                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                                                    aria-label={showConfirmPassword ? "Hide password" : "Reveal password"}
                                                    className="absolute right-3 top-1/2 -translate-y-1/2 text-stone-400 hover:text-stone-600 dark:hover:text-stone-200 text-xs transition-colors p-1"
                                                >
                                                    <i className={`fa-solid ${showConfirmPassword ? 'fa-eye-slash' : 'fa-eye'}`}></i>
                                                </button>
                                            </div>
                                        </div>
                                    </>
                                ) : (
                                    <>
                                        {mode === 'signup' && (
                                            <div>
                                                <label className="block text-xs font-semibold text-stone-500 uppercase tracking-wider mb-1">Display Name</label>
                                                <div className="relative">
                                                    <i className="fa-solid fa-user absolute left-3 top-1/2 -translate-y-1/2 text-stone-400 text-xs"></i>
                                                    <input 
                                                        type="text"
                                                        required
                                                        value={name}
                                                        onChange={(e) => setName(e.target.value)}
                                                        placeholder="John Doe"
                                                        className="w-full pl-9 pr-4 py-3 bg-white dark:bg-stone-800 border border-stone-300 dark:border-stone-700 rounded-xl text-sm focus:ring-2 focus:ring-amber-500/20 focus:border-amber-500 outline-none transition-all text-stone-900 dark:text-stone-100 placeholder:text-stone-400"
                                                    />
                                                </div>
                                            </div>
                                        )}

                                        <div>
                                            <label className="block text-xs font-semibold text-stone-500 uppercase tracking-wider mb-1">Email Address</label>
                                            <div className="relative">
                                                <i className="fa-solid fa-envelope absolute left-3 top-1/2 -translate-y-1/2 text-stone-400 text-xs"></i>
                                                <input 
                                                    type="email"
                                                    required
                                                    value={email}
                                                    onChange={(e) => setEmail(e.target.value)}
                                                    placeholder="you@example.com"
                                                    className="w-full pl-9 pr-4 py-3 bg-white dark:bg-stone-800 border border-stone-300 dark:border-stone-700 rounded-xl text-sm focus:ring-2 focus:ring-amber-500/20 focus:border-amber-500 outline-none transition-all text-stone-900 dark:text-stone-100 placeholder:text-stone-400"
                                                />
                                            </div>
                                        </div>

                                        {mode !== 'reset' && (
                                            <div>
                                                <div className="flex justify-between mb-1">
                                                    <label className="block text-xs font-semibold text-stone-500 uppercase tracking-wider">Password</label>
                                                    {mode === 'login' && (
                                                        <button 
                                                            type="button"
                                                            onClick={() => setMode('reset')}
                                                            className="text-[10px] text-amber-600 hover:underline"
                                                        >
                                                            <span>Forgot Password?</span>
                                                        </button>
                                                    )}
                                                </div>
                                                <div className="relative">
                                                    <i className="fa-solid fa-lock absolute left-3 top-1/2 -translate-y-1/2 text-stone-400 text-xs"></i>
                                                    <input 
                                                        type={showPassword ? "text" : "password"}
                                                        required
                                                        value={password}
                                                        onChange={(e) => setPassword(e.target.value)}
                                                        placeholder="••••••••"
                                                        className="w-full pl-9 pr-10 py-3 bg-white dark:bg-stone-800 border border-stone-300 dark:border-stone-700 rounded-xl text-sm focus:ring-2 focus:ring-amber-500/20 focus:border-amber-500 outline-none transition-all text-stone-900 dark:text-stone-100 placeholder:text-stone-400"
                                                    />
                                                    <button 
                                                        type="button"
                                                        onClick={() => setShowPassword(!showPassword)}
                                                        aria-label={showPassword ? "Hide password" : "Reveal password"}
                                                        className="absolute right-3 top-1/2 -translate-y-1/2 text-stone-400 hover:text-stone-600 dark:hover:text-stone-200 text-xs transition-colors p-1"
                                                    >
                                                        <i className={`fa-solid ${showPassword ? 'fa-eye-slash' : 'fa-eye'}`}></i>
                                                    </button>
                                                </div>
                                            </div>
                                        )}
                                    </>
                                )}

                                {authErr && (
                                    <div className={`p-3 rounded-lg text-xs leading-relaxed flex flex-col gap-2 ${authErr.startsWith('SUCCESS:') ? 'bg-emerald-50 text-emerald-700' : 'bg-red-50 text-red-600'}`}>
                                        <div className="flex items-start">
                                            <i className={`fa-solid ${authErr.startsWith('SUCCESS:') ? 'fa-circle-check' : 'fa-circle-exclamation'} mr-2 mt-0.5`}></i>
                                            <span>{authErr.replace('SUCCESS:', '').trim()}</span>
                                        </div>
                                        {authErr.includes('registered') && mode === 'signup' && (
                                            <button 
                                                type="button"
                                                onClick={() => {
                                                    setMode('login');
                                                    setAuthErr(null);
                                                }}
                                                className="self-end font-bold underline hover:no-underline"
                                            >
                                                <span>Switch to Login →</span>
                                            </button>
                                        )}
                                    </div>
                                )}

                                <button
                                    type="submit"
                                    disabled={loading}
                                    className="w-full h-12 bg-amber-600 hover:bg-amber-700 text-white rounded-xl font-bold transition-all shadow-md active:scale-95 disabled:opacity-50"
                                >
                                    {loading ? (
                                        <i className="fa-solid fa-circle-notch fa-spin"></i>
                                    ) : (
                                        <span>
                                            {mode === 'login' ? 'Login' : (mode === 'signup' ? 'Create Account' : (mode === 'confirmReset' ? 'Set New Password' : (mode === 'directChange' ? 'Change Password' : 'Send Reset Link')))}
                                        </span>
                                    )}
                                </button>

                                {mode !== 'confirmReset' && mode !== 'directChange' && (
                                    <div className="text-center mt-6">
                                        <button 
                                            type="button"
                                            onClick={() => setMode(mode === 'login' ? 'signup' : 'login')}
                                            className="text-stone-500 text-xs hover:text-amber-600"
                                        >
                                            <span>{mode === 'login' ? "Don't have an account? Sign up" : "Already have an account? Login"}</span>
                                        </button>
                                    </div>
                                )}
                            </motion.form>
                        )}
                    </AnimatePresence>
                </div>
            </motion.div>
        </div>
    );
};

export default AuthModal;
